/**
 * spotDraftStorage — persists spot mode draft across OAuth redirects
 *
 * Text fields (entity, location, notes, entityType) go to sessionStorage.
 * File objects go to IndexedDB (handles binary, no size limit issues).
 *
 * @atomic - Single purpose: draft persistence
 */

const DB_NAME = 'trckit_spot_draft'
const STORE_NAME = 'images'
const DB_VERSION = 1
const SESSION_KEY = 'trckit_spot_draft'
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours — discard stale drafts

// ─── IndexedDB helpers ────────────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function saveImagesToIDB(files) {
  if (!files?.length) return
  const db = await openDB()
  await clearIDB(db)
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    files.forEach((file, i) => {
      store.add({ blob: file, name: file.name, type: file.type, index: i })
    })
    tx.oncomplete = () => { db.close(); resolve() }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

async function loadImagesFromIDB() {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const req = store.getAll()
    req.onsuccess = () => {
      db.close()
      const rows = req.result || []
      rows.sort((a, b) => a.index - b.index)
      const files = rows.map(r => new File([r.blob], r.name, { type: r.type }))
      resolve(files)
    }
    req.onerror = () => { db.close(); reject(req.error) }
  })
}

async function clearIDB(db) {
  const openedHere = !db
  if (openedHere) db = await openDB()
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).clear()
    tx.oncomplete = () => { if (openedHere) db.close(); resolve() }
    tx.onerror = () => { if (openedHere) db.close(); resolve() /* best-effort */ }
  })
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Save draft before deactivating spot mode.
 * @param {{ entity, location, notes, entityType, images: File[] }} draft
 */
export async function saveDraft({ entity, location, notes, entityType, images }) {
  try {
    const meta = { entity, location, notes, entityType, savedAt: Date.now() }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(meta))
    await saveImagesToIDB(images)
  } catch {
    // Best-effort — draft may be partial on restore
  }
}

/**
 * Load saved draft. Returns null if none exists.
 * @returns {Promise<{ entity, location, notes, entityType, images: File[] } | null>}
 */
export async function loadDraft() {
  const raw = sessionStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    const meta = JSON.parse(raw)
    // Discard drafts older than TTL (stale images in IDB, abandoned sessions)
    if (meta.savedAt && Date.now() - meta.savedAt > DRAFT_TTL_MS) {
      await clearDraft()
      return null
    }
    const images = await loadImagesFromIDB().catch(() => [])
    return { ...meta, images }
  } catch {
    return null
  }
}

/** Remove draft from all storage after a successful restore. */
export async function clearDraft() {
  sessionStorage.removeItem(SESSION_KEY)
  await clearIDB(null)
}
